import { useEffect, useRef, useState, memo, useMemo, useCallback } from 'react';

const testimonials = [
  {
    name: 'Mark & Lisa Thompson',
    location: 'Kaysville, UT',
    text: "These guys did a great job cleaning up and fixing my home after a pretty nasty flood. I would highly recommend to anyone who suffers water damage like I did. They were professional, fast, and made a stressful situation so much easier.",
    rating: 5,
  },
  {
    name: 'Jennifer Martinez',
    location: 'Clearfield, UT',
    text: "Davis County Disaster Cleanup responded within an hour when our basement flooded at 2 AM. Their team worked tirelessly to extract the water and save our belongings. The restoration was flawless - you'd never know there was damage. Highly recommend!",
    rating: 5,
  },
  {
    name: 'David & Sarah Chen',
    location: 'Layton, UT',
    text: 'After discovering mold in our bathroom, Davis County Disaster Cleanup handled everything professionally. They explained the process clearly, worked efficiently, and the results exceeded our expectations. Our home feels brand new and safe again!',
    rating: 5,
  },
  {
    name: 'Robert Williams',
    location: 'Farmington, UT',
    text: 'The team is simply outstanding. They restored our living room after water damage from a burst pipe. The communication throughout the project was excellent, and the quality of work is top-notch. True professionals.',
    rating: 5,
  },
  {
    name: 'Amanda Foster',
    location: 'Syracuse, UT',
    text: 'After experiencing smoke damage, I was worried about the lingering smell. Davis County Disaster Cleanup completely eliminated all odors and restored our home beautifully. Their team was professional and respectful throughout the entire process.',
    rating: 5,
  },
  {
    name: 'Michael & Karen Davis',
    location: 'Bountiful, UT',
    text: "We had a sewage backup that was a nightmare. Davis County Disaster Cleanup came out immediately, handled the cleanup professionally, and made sure everything was sanitized properly. They even helped us with our insurance claim. Couldn't ask for better service!",
    rating: 5,
  },
];

const platforms = [
  { platform: 'Google', reviews: '4.7-Star Rating', rating: '4.7' },
  { platform: 'Yelp', reviews: 'Recommended', rating: '5.0' },
  { platform: 'Facebook', reviews: 'Top Rated', rating: '5.0' },
  { platform: 'BBB', reviews: 'Accredited', rating: 'A+' },
];

const TestimonialCard = memo(({ testimonial }: { testimonial: typeof testimonials[0] }) => (
  <div className="flex-shrink-0 w-[320px] sm:w-[380px] bg-gray-50 rounded-2xl p-6 lg:p-8 shadow-lg mx-3">
    <div className="flex gap-1 mb-4">
      {[...Array(testimonial.rating)].map((_, i) => (
        <i key={i} className="ri-star-fill text-xl text-sky-500"></i>
      ))}
    </div>
    <p className="text-gray-700 text-lg leading-relaxed mb-6">&quot;{testimonial.text}&quot;</p>
    <div>
      <p className="text-gray-900 font-semibold text-lg">{testimonial.name}</p>
      <p className="text-gray-500 text-base">{testimonial.location}</p>
    </div>
  </div>
));

TestimonialCard.displayName = 'TestimonialCard';

const PlatformCard = memo(({ item }: { item: typeof platforms[0] }) => (
  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md text-center hover:shadow-lg transition-shadow">
    <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 flex items-center justify-center bg-gray-100 rounded-full">
      <i
        className={`text-xl sm:text-2xl text-gray-700 ${
          item.platform === 'Google'
            ? 'ri-google-fill'
            : item.platform === 'Yelp'
            ? 'ri-star-smile-fill'
            : item.platform === 'Facebook'
            ? 'ri-facebook-fill'
            : 'ri-award-fill'
        }`}
      ></i>
    </div>
    <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{item.platform}</p>
    <div className="flex justify-center gap-0.5 sm:gap-1 mb-1 sm:mb-2">
      {item.rating !== 'A+' ? (
        [...Array(5)].map((_, i) => (
          <i key={i} className="ri-star-fill text-xs sm:text-sm text-sky-500"></i>
        ))
      ) : (
        <span className="text-xl sm:text-2xl font-bold text-sky-600">A+</span>
      )}
    </div>
    <p className="text-gray-600 text-[10px] sm:text-xs">{item.reviews}</p>
  </div>
));

PlatformCard.displayName = 'PlatformCard';

function Testimonials() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    const el = sectionRef.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      (document.getElementById('name') as HTMLInputElement)?.focus();
    }, 500);
  }, []);

  return (
    <section id="testimonials" ref={sectionRef} className="py-16 sm:py-24 bg-white overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-sky-600 text-lg font-semibold mb-2">Customer Reviews</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            What Our Customers Say About Us
          </h2>
          <p className="text-gray-600 text-lg sm:text-xl leading-relaxed max-w-4xl mx-auto text-center">
            Read testimonials from Utah homeowners who trusted Davis County Disaster Cleanup to restore their properties after disaster struck.
          </p>
        </div>
      </div>

      <div
        className={`relative overflow-hidden transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="flex animate-scroll hover:[animation-play-state:paused]">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={`first-${index}`} testimonial={testimonial} />
          ))}
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={`second-${index}`} testimonial={testimonial} />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`mt-10 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {platforms.map((item, index) => (
            <PlatformCard key={index} item={item} />
          ))}
        </div>

        <div className="mt-10 sm:mt-16 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
          <button
            onClick={scrollToContact}
            className="w-full sm:w-auto bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-sky-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Your Free Assessment
          </button>
          <a
            href="tel:3853406632"
            className="w-full sm:w-auto text-center border border-gray-300 text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Call Now: (385) 340-6632
          </a>
        </div>
      </div>

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 40s linear infinite;
        }
      `}</style>
    </section>
  );
}

export default memo(Testimonials);
